import { supabase } from "@/integrations/supabase/client";

export interface AccountTransaction {
  id: string;
  user_id: string;
  account_id: string;
  transaction_type: 'credit' | 'debit' | 'adjustment';
  amount: number;
  balance_after: number;
  description: string;
  reference_type: 'order' | 'expense' | 'transfer' | 'manual' | 'opening_balance';
  reference_id: string | null;
  transaction_date: string;
  created_at: string;
  created_by: string | null;
}

export interface AccountBalanceSummary {
  id: string;
  user_id: string;
  account_name: string;
  account_type: 'asset' | 'liability' | 'equity';
  current_balance: number;
  currency: string;
  transaction_count: number;
  last_transaction_date: string | null;
  total_credits: number;
  total_debits: number;
}

/**
 * Fetch transaction history for a specific account
 */
export async function fetchAccountTransactions(
  accountId: string,
  startDate?: Date,
  endDate?: Date
): Promise<AccountTransaction[]> {
  try {
    const { data: { session } } = await supabase.auth.getSession();
    if (!session?.user?.id) {
      throw new Error('User not authenticated');
    }

    let query = supabase
      .from('account_transactions')
      .select('*')
      .eq('user_id', session.user.id)
      .eq('account_id', accountId)
      .order('transaction_date', { ascending: false });

    if (startDate) {
      query = query.gte('transaction_date', startDate.toISOString());
    }

    if (endDate) {
      query = query.lte('transaction_date', endDate.toISOString());
    }

    const { data, error } = await query;

    if (error) throw error;
    return data || [];
  } catch (error) {
    console.error('Error fetching account transactions:', error);
    throw error;
  }
}

/**
 * Fetch account balance summary with statistics
 */
export async function fetchAccountBalanceSummary(): Promise<AccountBalanceSummary[]> {
  try {
    const { data: { session } } = await supabase.auth.getSession();
    if (!session?.user?.id) {
      throw new Error('User not authenticated');
    }

    const { data, error } = await supabase
      .from('account_balance_summary')
      .select('*')
      .eq('user_id', session.user.id)
      .order('account_name');

    if (error) throw error;
    return data || [];
  } catch (error) {
    console.error('Error fetching account balance summary:', error);
    throw error;
  }
}

/**
 * Create a manual transaction (adjustment)
 */
export async function createManualTransaction(
  accountId: string,
  amount: number,
  transactionType: 'credit' | 'debit' | 'adjustment',
  description: string
): Promise<string> {
  try {
    const { data: { session } } = await supabase.auth.getSession();
    if (!session?.user?.id) {
      throw new Error('User not authenticated');
    }

    // Get current balance
    const { data: account, error: accountError } = await supabase
      .from('financial_accounts')
      .select('current_balance')
      .eq('id', accountId)
      .eq('user_id', session.user.id)
      .single();

    if (accountError) throw accountError;

    // Calculate new balance
    let balanceAfter = account.current_balance;
    if (transactionType === 'credit') {
      balanceAfter += amount;
    } else if (transactionType === 'debit') {
      balanceAfter -= amount;
    } else {
      // For adjustment, the amount IS the new balance
      balanceAfter = amount;
    }

    // Insert transaction
    const { data, error } = await supabase
      .from('account_transactions')
      .insert({
        user_id: session.user.id,
        account_id: accountId,
        transaction_type: transactionType,
        amount: transactionType === 'adjustment' ? Math.abs(amount - account.current_balance) : amount,
        balance_after: balanceAfter,
        description,
        reference_type: 'manual',
        transaction_date: new Date().toISOString(),
        created_by: session.user.id
      })
      .select('id')
      .single();

    if (error) throw error;
    return data.id;
  } catch (error) {
    console.error('Error creating manual transaction:', error);
    throw error;
  }
}

/**
 * Record opening balance for an account (one-time setup)
 */
export async function recordOpeningBalance(
  accountId: string,
  openingBalance: number,
  description: string = 'Opening balance'
): Promise<string> {
  try {
    const { data: { session } } = await supabase.auth.getSession();
    if (!session?.user?.id) {
      throw new Error('User not authenticated');
    }

    // Check if opening balance already exists
    const { data: existing } = await supabase
      .from('account_transactions')
      .select('id')
      .eq('account_id', accountId)
      .eq('reference_type', 'opening_balance')
      .single();

    if (existing) {
      throw new Error('Opening balance already set for this account');
    }

    // Insert opening balance transaction
    const { data, error } = await supabase
      .from('account_transactions')
      .insert({
        user_id: session.user.id,
        account_id: accountId,
        transaction_type: openingBalance >= 0 ? 'credit' : 'debit',
        amount: Math.abs(openingBalance),
        balance_after: openingBalance,
        description,
        reference_type: 'opening_balance',
        transaction_date: new Date().toISOString(),
        created_by: session.user.id
      })
      .select('id')
      .single();

    if (error) throw error;
    return data.id;
  } catch (error) {
    console.error('Error recording opening balance:', error);
    throw error;
  }
}

/**
 * Update financial account details
 */
export async function updateFinancialAccount(
  accountId: string,
  updates: {
    account_name?: string;
    account_type?: 'asset' | 'liability' | 'equity';
    is_active?: boolean;
  }
): Promise<void> {
  try {
    const { data: { session } } = await supabase.auth.getSession();
    if (!session?.user?.id) {
      throw new Error('User not authenticated');
    }

    const { error } = await supabase
      .from('financial_accounts')
      .update(updates)
      .eq('id', accountId)
      .eq('user_id', session.user.id);

    if (error) throw error;
  } catch (error) {
    console.error('Error updating financial account:', error);
    throw error;
  }
}

/**
 * Delete financial account (only if no transactions)
 */
export async function deleteFinancialAccount(accountId: string): Promise<void> {
  try {
    const { data: { session } } = await supabase.auth.getSession();
    if (!session?.user?.id) {
      throw new Error('User not authenticated');
    }

    // Check for existing transactions
    const { count } = await supabase
      .from('account_transactions')
      .select('*', { count: 'exact', head: true })
      .eq('account_id', accountId);

    if (count && count > 0) {
      throw new Error('Cannot delete account with existing transactions. Mark as inactive instead.');
    }

    const { error } = await supabase
      .from('financial_accounts')
      .delete()
      .eq('id', accountId)
      .eq('user_id', session.user.id);

    if (error) throw error;
  } catch (error) {
    console.error('Error deleting financial account:', error);
    throw error;
  }
}
